import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1622015663319-e97e697503ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQ4Njk2NDl8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const PRICING = [
  {
    tier: "Basic",
    tagline: "Presence",
    price: "$1,500",
    period: "/month",
    desc: "Establish your property's digital foundation with polished grid management and aesthetic alignment.",
    features: [
      "OTA profile optimization (Airbnb, Vrbo, Booking)",
      "Instagram grid curation & aesthetic alignment",
      "Monthly content calendar",
      "Hashtag & SEO keyword strategy",
      "Monthly performance report",
    ],
    cta: "Get Started",
    highlight: false,
  },
  {
    tier: "Growth",
    tagline: "Momentum",
    price: "$3,500",
    period: "/month",
    desc: "Build momentum with reels, influencer outreach, and active community engagement.",
    features: [
      "Everything in Basic",
      "8x Reels/month (shot + edited)",
      "Influencer outreach & collaboration",
      "Engagement & community management",
      "Story strategy & highlights curation",
      "Bi-weekly strategy calls",
    ],
    cta: "Apply Now",
    highlight: true,
  },
  {
    tier: "Aggressive",
    tagline: "Scale",
    price: "$7,000",
    period: "/month",
    desc: "Full-throttle growth with paid ads, direct booking funnels, and PR placements.",
    features: [
      "Everything in Growth",
      "Meta & Google Ads management",
      "Direct booking funnel setup",
      "Email marketing automation",
      "PR & media outreach",
      "Dedicated account strategist",
      "Weekly performance reviews",
    ],
    cta: "Apply Now",
    highlight: false,
  },
];

export default function ServiceOTA() {
  return (
    <div>
      {/* ========== HERO (DARK) ========== */}
      <section
        data-testid="ota-hero-section"
        className="relative min-h-[80vh] flex items-center bg-dark_base overflow-hidden"
      >
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-dark_base/70 via-dark_base/50 to-dark_base" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-6 font-body">
              Hospitality Growth
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl md:text-6xl lg:text-[5rem] leading-[1.05] tracking-tight font-extrabold text-cream mb-8">
              Stop Relying on{" "}
              <span className="font-accent italic font-normal text-mint">Algorithms.</span>{" "}
              Start Driving Direct Bookings.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-lg md:text-xl leading-relaxed text-cream/70 max-w-2xl font-body">
              We shift your property from platform dependence to a powerful, self-sustaining direct booking engine through strategic social media and conversion funnels.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ========== PRICING (LIGHT) ========== */}
      <section className="bg-light_base -mt-12 rounded-t-[3rem] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Packages
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-4xl md:text-5xl leading-tight tracking-tight font-bold text-dark_base mb-6">
              Choose Your <span className="font-accent italic font-normal text-mint">Growth Path</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="font-body text-base md:text-lg text-dark_base/60 max-w-2xl mb-16">
              Three tiers designed to match where you are and take you where you want to be.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-testid="pricing-grid">
            {PRICING.map((plan, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                data-testid={`pricing-card-${plan.tier.toLowerCase()}`}
                className={`rounded-3xl p-8 md:p-10 flex flex-col ${
                  plan.highlight
                    ? "bg-dark_base text-cream border-2 border-mint shadow-2xl relative"
                    : "bg-white border border-dark_base/5 hover:shadow-lg transition-shadow duration-500"
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-mint text-dark_base text-xs font-bold px-4 py-1.5 rounded-full tracking-wider uppercase">
                    Most Popular
                  </div>
                )}

                <div className="mb-8">
                  <p className={`text-xs tracking-[0.2em] uppercase font-bold mb-2 font-body ${plan.highlight ? "text-mint" : "text-mint"}`}>
                    {plan.tagline}
                  </p>
                  <h3 className={`font-heading text-2xl font-semibold mb-3 ${plan.highlight ? "text-cream" : "text-dark_base"}`}>
                    {plan.tier}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className={`font-heading text-4xl font-extrabold ${plan.highlight ? "text-cream" : "text-dark_base"}`}>
                      {plan.price}
                    </span>
                    <span className={`font-body text-sm ${plan.highlight ? "text-cream/50" : "text-dark_base/40"}`}>
                      {plan.period}
                    </span>
                  </div>
                  <p className={`font-body text-sm leading-relaxed ${plan.highlight ? "text-cream/60" : "text-dark_base/60"}`}>
                    {plan.desc}
                  </p>
                </div>

                <ul className="space-y-3 mb-10 flex-grow">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${plan.highlight ? "text-mint" : "text-mint"}`} />
                      <span className={`font-body text-sm ${plan.highlight ? "text-cream/80" : "text-dark_base/70"}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${plan.tier.toLowerCase()}`}
                  className={`py-4 px-8 rounded-full text-center font-bold font-body text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                    plan.highlight
                      ? "bg-mint text-dark_base hover:bg-mint-hover"
                      : "bg-transparent border border-dark_base/20 text-dark_base hover:bg-dark_base hover:text-light_base"
                  }`}
                >
                  {plan.cta} <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ========== PROPERTY AUDIT CTA ========== */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-dark_base rounded-3xl p-12 md:p-20 text-center"
          >
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-mint mb-4 font-body">
              Free Assessment
            </p>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight tracking-tight font-bold text-cream mb-4">
              Not Sure Where to <span className="font-accent italic font-normal text-mint">Start?</span>
            </h2>
            <p className="font-body text-cream/60 text-base md:text-lg max-w-xl mx-auto mb-10">
              Apply for a complimentary property audit. We'll analyze your current presence and deliver a custom growth roadmap.
            </p>
            <Link
              to="/contact"
              data-testid="ota-audit-cta"
              className="inline-flex bg-mint text-dark_base font-bold py-4 px-10 rounded-full hover:bg-mint-hover transition-colors duration-300 font-body items-center gap-2"
            >
              Apply for a Property Audit <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
